import React, { useEffect, useState } from "react";
import { BsArrowRepeat, BsCalendar2Event, BsGearWideConnected, BsPeople } from "react-icons/bs";
import { getSupportStats } from "../../services/dashboard/dashboard";
import { monthlyLeaderboard, sumSales } from "./support_sales_metrics";
import { Amount, CountBadge, DonutCard, Leaderboard, SummaryCard, SupportCard, formatNumber } from "./support_sales_widgets";
import { CHART_COLORS } from "./charts/palette";
import SaleRows from "./support_sales_rows";
import SimotelCallStats from "./simotel_call_stats";

export default function SupportSalesDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  const [period, setPeriod] = useState("month");
  const [measure, setMeasure] = useState("amount");

  useEffect(() => {
    const controller = new AbortController();

    async function load() {
      setLoading(true);
      setError("");

      try {
        const response = await getSupportStats(controller.signal);
        if (response?.status !== 200 || !Array.isArray(response.data?.items)) {
          throw new Error("Invalid support statistics response");
        }
        if (!controller.signal.aborted) {
          setData(response.data);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          setError("دریافت آمار فروش پشتیبان‌ها انجام نشد.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    load();
    return () => controller.abort();
  }, [reload]);

  const items = data?.items || [];
  const supports = items.filter((item) => Number(item.support_id) > 0);
  const totals = { today: sumSales(items, "today"), month: sumSales(items, "month") };
  const assigned = sumSales(supports, period);
  const unassigned = sumSales(items.filter((item) => Number(item.support_id) === 0), period);
  const ranking = monthlyLeaderboard(items);
  const periodLabel = period === "month" ? "این ماه" : "امروز";
  const updatedAt = new Date(data?.updated_at);
  const updatedLabel = Number.isNaN(updatedAt.getTime()) ? "" : new Intl.DateTimeFormat("fa-IR", {
    hour: "2-digit", minute: "2-digit", timeZone: "Asia/Tehran",
  }).format(updatedAt);

  return (
    <section
      dir="rtl"
      aria-labelledby="support-sales-title"
      aria-busy={loading}
      className="w-full space-y-5 p-1 text-app-text sm:p-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="mb-1 text-xs font-medium text-app-text-muted">نمای کلی عملکرد</p>
          <h2 id="support-sales-title" className="text-xl font-bold text-app-text sm:text-2xl">داشبورد فروش پشتیبان‌ها</h2>
          <p className="mt-2 text-xs leading-6 text-app-text-muted">
            امروز و ماه جاری شمسی · مبالغ به ریال
            {updatedLabel && <span className="mr-3">به‌روزرسانی: {updatedLabel}</span>}
          </p>
        </div>
        <button
          type="button"
          disabled={loading}
          onClick={() => setReload((value) => value + 1)}
          className="inline-flex items-center gap-2 rounded-lg border border-app-border bg-app-surface px-3 py-2 text-sm text-app-text shadow-sm transition-colors hover:bg-app-muted disabled:opacity-50"
        >
          <BsArrowRepeat aria-hidden="true" />
          به‌روزرسانی
        </button>
      </div>

      {loading ? (
        <p role="status" className="rounded-xl border border-app-border bg-app-surface py-16 text-center text-app-text-muted">
          در حال دریافت آمار…
        </p>
      ) : error ? (
        <div role="alert" className="rounded-xl border border-red-500/20 bg-app-surface py-12 text-center text-red-600">
          <p>{error}</p>
          <button
            type="button"
            onClick={() => setReload((value) => value + 1)}
            className="mt-3 rounded-lg border border-red-500/20 px-3 py-2 text-sm"
          >
            تلاش دوباره
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryCard title="خدمات این ماه" metric={totals.month.services} icon={<BsGearWideConnected />}>
              <SaleRows title="خدمات این ماه" entries={data?.details?.month?.services} />
            </SummaryCard>
            <SummaryCard title="خدمات امروز" metric={totals.today.services} icon={<BsGearWideConnected />}>
              <SaleRows title="خدمات امروز" entries={data?.details?.today?.services} />
            </SummaryCard>
            <SummaryCard title="مشاوره‌های این ماه" metric={totals.month.reservations} icon={<BsCalendar2Event />} teal>
              <SaleRows title="مشاوره‌های این ماه" entries={data?.details?.month?.reservations} consultation />
            </SummaryCard>
            <SummaryCard title="مشاوره‌های امروز" metric={totals.today.reservations} icon={<BsCalendar2Event />} teal>
              <SaleRows title="مشاوره‌های امروز" entries={data?.details?.today?.reservations} consultation />
            </SummaryCard>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
            <div role="group" aria-label="بازه نمایش نمودارها و کارت‌ها" className="inline-flex rounded-lg border border-app-border bg-app-surface p-1">
              {[["month", "این ماه"], ["today", "امروز"]].map(([value, label]) => (
                <button key={value} type="button" aria-pressed={period === value} onClick={() => setPeriod(value)}
                  className={`rounded-md px-4 py-2 text-xs font-medium transition-colors ${period === value ? "bg-app-primary text-app-on-primary" : "text-app-text-muted hover:bg-app-muted"}`}>
                  {label}
                </button>
              ))}
            </div>
            <div role="group" aria-label="مبنای نمودارها" className="flex items-center gap-1 text-xs">
              <span className="ml-2 text-app-text-muted">نمایش نمودار بر اساس</span>
              {[["amount", "مبلغ"], ["count", "تعداد"]].map(([value, label]) => (
                <button key={value} type="button" aria-pressed={measure === value} onClick={() => setMeasure(value)}
                  className={`rounded-md px-3 py-2 font-medium transition-colors ${measure === value ? "bg-app-primary-soft text-app-primary" : "text-app-text-muted hover:bg-app-muted"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 items-stretch gap-4 md:grid-cols-2 xl:grid-cols-3">
            <DonutCard title="ترکیب فروش" periodLabel={periodLabel} measure={measure} segments={[
              { label: "خدمات", value: totals[period].services[measure], color: CHART_COLORS.pink },
              { label: "مشاوره‌های پرداخت‌شده", value: totals[period].reservations[measure], color: CHART_COLORS.teal },
            ]} />
            <DonutCard title="انتساب فروش" periodLabel={periodLabel} measure={measure} segments={[
              { label: "منسوب به پشتیبان", value: assigned.total[measure], color: CHART_COLORS.cyan },
              { label: "بدون پشتیبان", value: unassigned.total[measure], color: CHART_COLORS.amber },
            ]} />
            <Leaderboard items={ranking} />
          </div>

          <SimotelCallStats calls={data?.calls} period={period} />

          <section aria-label="فروش بدون پشتیبان" className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-3">
            <div>
              <h3 className="text-sm font-semibold text-app-text">فروش بدون پشتیبان · {periodLabel}</h3>
              <p className="mt-1 text-xs leading-6 text-app-text-muted">در مجموع فروش و نمودارها محاسبه شده؛ در رتبه‌بندی پشتیبان‌ها وارد نمی‌شود.</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Amount value={unassigned.total.amount} className="font-bold text-app-text" />
              <CountBadge count={unassigned.total.count} />
            </div>
          </section>

          <section aria-labelledby="support-performance-title">
            <div className="mb-3 mt-6 flex flex-wrap items-center justify-between gap-2">
              <h3 id="support-performance-title" className="flex items-center gap-2 text-base font-semibold text-app-text">
                <BsPeople className="text-blue-500" aria-hidden="true" /> عملکرد پشتیبان‌ها
              </h3>
              <span className="text-xs text-app-text-muted">{formatNumber(supports.length)} پشتیبان · {periodLabel}</span>
            </div>
            {supports.length === 0 ? (
              <p className="rounded-xl border border-app-border bg-app-surface p-6 text-center text-sm text-app-text-muted">پشتیبانی برای نمایش وجود ندارد.</p>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {supports.map((item) => (
                  <SupportCard key={item.support_id} item={item} period={period} assignedTotal={assigned.total.amount} />
                ))}
              </div>
            )}
          </section>
          <p className="text-xs leading-6 text-app-text-subtle">
            مبلغ خدمات، ارزش کامل توافق‌شده پس از تخفیف با مالیات است؛ مبلغ مشاوره‌ها، خالص پرداخت ثبت‌شده است.
            رتبه‌بندی، ارزش فروش را نشان می‌دهد و معادل وجه وصول‌شده خدمات نیست.
          </p>
        </>
      )}
    </section>
  );
}
